import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { storeApi, useStore } from "./StoreContext";
import { useDocumentTitle } from "./StorefrontLayout";
import { ShoppingBag, Plus, Minus, Truck, Package, ShieldCheck, ChevronLeft, PawPrint } from "lucide-react";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function ProductDetail() {
  const { pid } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const { addToCart } = useStore();

  useDocumentTitle(p ? `${p.name} — Buy at Furmigo` : "Loading product…",
    p ? `${p.description?.slice(0, 150) || `${p.name} — premium pet essentials at Furmigo. Free shipping over ₹1,499.`}` : "");

  useEffect(() => { storeApi.product(pid).then(setP).catch(() => setP(false)); }, [pid]);

  if (p === false) return <div className="max-w-7xl mx-auto px-4 py-16 text-center"><PawPrint className="w-12 h-12 mx-auto text-stone-300 mb-2" /><div className="font-heading text-xl">Product not found</div><Link to="/products" className="text-amber-700 font-semibold mt-3 inline-block">← Back to shop</Link></div>;
  if (!p) return <div className="max-w-7xl mx-auto px-4 py-8 text-stone-500">Loading…</div>;

  const img = p.image_path ? `${process.env.REACT_APP_BACKEND_URL}/api/files-public/${p.image_path}` : null;
  const onAdd = () => { addToCart(p, qty); nav("/cart"); };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-4" data-testid="product-detail-page">
      <Link to="/products" className="text-sm text-stone-500 hover:text-stone-900 inline-flex items-center gap-1 mb-3"><ChevronLeft className="w-4 h-4" />Back to shop</Link>
      <div className="grid sm:grid-cols-2 gap-6 sm:gap-10">
        <div className="rounded-3xl bg-white overflow-hidden border border-stone-200 aspect-square">
          {img ? <img src={img} alt={p.name} className="w-full h-full object-cover" /> :
            <div className="w-full h-full grid place-items-center text-stone-300"><PawPrint className="w-24 h-24" /></div>}
        </div>
        <div>
          {p.brand?.name && <div className="text-xs uppercase tracking-[0.2em] text-amber-700 font-semibold">{p.brand.name}</div>}
          <h1 className="font-heading text-3xl sm:text-4xl font-black mt-1.5">{p.name}</h1>
          <div className="mt-4 flex items-baseline gap-3">
            <div className="font-heading text-3xl font-black">{inr(p.sale_price)}</div>
            {p.mrp && p.mrp > p.sale_price && <div className="text-stone-400 line-through">{inr(p.mrp)}</div>}
          </div>
          <div className="mt-1 text-xs text-stone-500">Inclusive of all taxes</div>
          {p.in_stock ? (
            <div className="mt-3 text-sm text-emerald-700 font-semibold">In stock · ready to ship</div>
          ) : (
            <div className="mt-3 text-sm text-rose-700 font-semibold">Currently out of stock</div>
          )}
          {p.description && (
            <p className="mt-4 text-stone-700 text-sm leading-relaxed whitespace-pre-line">{p.description}</p>
          )}

          <div className="mt-6 flex items-center gap-3 flex-wrap">
            <div className="flex items-center border border-stone-300 rounded-full">
              <button onClick={() => setQty(q => Math.max(1, q - 1))} className="p-2 hover:bg-stone-100 rounded-full" data-testid="qty-minus"><Minus className="w-4 h-4" /></button>
              <span className="px-3 font-bold" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(q => q + 1)} className="p-2 hover:bg-stone-100 rounded-full" data-testid="qty-plus"><Plus className="w-4 h-4" /></button>
            </div>
            <button onClick={onAdd} disabled={!p.in_stock} className="flex-1 sm:flex-none bg-stone-900 disabled:bg-stone-300 text-white px-6 py-3 rounded-full font-semibold inline-flex items-center justify-center gap-2 hover:bg-stone-800" data-testid="pd-add-to-cart">
              <ShoppingBag className="w-4 h-4" />Add to cart
            </button>
          </div>

          <div className="mt-8 grid sm:grid-cols-3 gap-3 text-xs">
            <Feature icon={<Truck className="w-4 h-4" />} title="Free shipping" sub="On orders over ₹1,499" />
            <Feature icon={<Package className="w-4 h-4" />} title="Easy returns" sub="7-day no-questions" />
            <Feature icon={<ShieldCheck className="w-4 h-4" />} title="Genuine brands" sub="100% authentic" />
          </div>
        </div>
      </div>
    </div>
  );
}

const Feature = ({ icon, title, sub }) => (
  <div className="rounded-xl border border-stone-200 p-3 flex items-start gap-2">
    <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 grid place-items-center shrink-0">{icon}</div>
    <div><div className="font-semibold">{title}</div><div className="text-stone-500">{sub}</div></div>
  </div>
);
